<!-- This example requires Tailwind CSS v2.0+ -->
<template>
  <div>
    <PageHeader :title="$t('Your protection plans')" />
    <MigrationConsentBanner
      v-for="consent in pendingConsents"
      :key="consent.id"
      :consent="consent"
      :remaining-days="remainingDays(consent)"
      @open-consent="openConsentModal"
    />

    <MigrationConsentModal
      v-if="selectedConsent && selectedPlan"
      :open="showConsentModal"
      :consent="selectedConsent"
      :plan="selectedPlan"
      @close="closeConsentModal"
    />

    <div>
      <ErrorBanner v-if="aPlanIsSuspended" error-level="error">
        {{ $t("One of your contracts has been suspended, please settle your situation from the Invoices tab") }}
      </ErrorBanner>
      <ErrorBanner v-else-if="!allPaymentsUpToDate" error-level="error">
        {{ $t("You are not up to date with your payments, please settle your situation from the Invoices tab") }}
      </ErrorBanner>
    </div>
    <ul role="list" class="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
      <li
        v-for="(plan, id) in plans"
        :key="id"
        :class="[
          isActive(plan) ? 'bg-white' : ' opacity-70',
          'col-span-1 flex flex-col text-center rounded-lg shadow-sm divide-y divide-gray-200',
        ]"
      >
        <div class="flex-1 flex flex-col p-8">
          <img class="max-h-32 object-contain shrink-0 mx-auto" :src="plan.imageUrl" alt="" />
          <h3 class="mt-2 text-gray-900 text-sm font-medium">{{ plan.title }}</h3>
          <p class="text-xs text-gray-400">{{ plan.friendlyId }}</p>
          <dl class="mt-1 grow flex flex-col justify-between">
            <dd v-if="hasBreakdownCoverage(plan) || plan.recurring" class="text-gray-500 text-sm flex items-center">
              {{ $t("Term length") }} : {{ warrantyTerm(plan) }}
              <VTooltip v-if="hasBreakdownCoverage(plan)" theme="info-tooltip">
                <InformationCircleIcon
                  class="cursor-pointer h-5 w-4 text-gray-400 inline-block ml-1"
                  aria-hidden="true"
                />
                <template #popper>
                  <span>
                    La garantie panne prend le relais à la fin de la garantie légale ou commerciale du fabricant, elle
                    vous couvre ensuite pendant {{ warrantyTerm(plan) }}
                  </span>
                </template>
              </VTooltip>
            </dd>
            <dd v-else class="text-gray-500 text-sm">{{ $t("Expires on") }} {{ plan.expirationDate }}</dd>
            <div v-if="!paymentsUpToDate(plan)">
              <dd class="mt-3">
                <ExclamationIcon class="text-red-400 w-8 h-8 mx-auto" :aria-label="$t('Plan awaiting payment')" />
              </dd>
            </div>
            <dt class="sr-only">{{ $t("Status") }}</dt>
            <dd class="mt-3">
              <span
                :class="[
                  isActive(plan) ? 'text-green-800 bg-green-100' : 'text-gray-800 bg-gray-100',
                  'px-2 py-1 text-xs font-medium rounded-full',
                ]"
              >
                {{ $t(statusLabel(plan)) }}
              </span>
              <div v-if="plan.scheduledCancellation?.date" class="mt-1 text-xs text-gray-500">
                Résiliation programmée le {{ plan.scheduledCancellation.date }}
              </div>
            </dd>
          </dl>
        </div>
        <div>
          <div class="-mt-px flex divide-x divide-gray-200">
            <div class="cursor-pointer w-0 flex-1 flex">
              <router-link
                :to="`/plans/${plan.friendlyId}`"
                class="relative -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-bl-lg hover:text-gray-500"
              >
                <DocumentTextIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
                <span class="ml-3">{{ $t("View plan") }}</span>
              </router-link>
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { DocumentTextIcon, ExclamationIcon, InformationCircleIcon } from "@heroicons/vue/solid";
import { PageHeader } from "@estaly/ui";
import { mapActions, mapGetters, mapState } from "vuex";
import ErrorBanner from "@/components/utils/ErrorBanner.vue";
import MigrationConsentBanner from "@/components/contractMigration/MigrationConsentBanner.vue";
import MigrationConsentModal from "@/components/contractMigration/MigrationConsentModal.vue";

export default {
  components: {
    PageHeader,
    ErrorBanner,
    ExclamationIcon,
    DocumentTextIcon,
    InformationCircleIcon,
    MigrationConsentBanner,
    MigrationConsentModal,
  },

  data() {
    return {
      selectedConsent: null,
      showConsentModal: false,
    };
  },

  computed: {
    ...mapState("auth", ["signedIn"]),
    ...mapState("customer", ["plans"]),
    ...mapGetters("planSales", [
      "warrantyTerm",
      "paymentsUpToDate",
      "isActive",
      "isSuspended",
      "hasBreakdownCoverage",
      "statusLabel",
    ]),
    ...mapGetters("contractMigrationConsents", ["pendingConsents", "remainingDays"]),
    allPaymentsUpToDate() {
      return this.plans.every((plan) => this.paymentsUpToDate(plan));
    },
    aPlanIsSuspended() {
      return this.plans.some((plan) => this.isSuspended(plan));
    },
    selectedPlan() {
      if (!this.selectedConsent) return null;
      return this.plans.find((plan) => plan.id === this.selectedConsent.planSaleId) || null;
    },
  },

  async created() {
    if (this.signedIn) {
      await this.getPlans();
      this.fetchConsents();
    } else {
      this.$router.push("/signin");
    }
  },

  methods: {
    ...mapActions("customer", ["getPlans"]),
    ...mapActions("notifications", ["notify"]),
    ...mapActions("contractMigrationConsents", ["fetchConsents"]),

    openConsentModal(consent) {
      this.selectedConsent = consent;
      this.showConsentModal = true;
    },

    async closeConsentModal() {
      this.showConsentModal = false;
      this.selectedConsent = null;
      await this.fetchConsents();
    },
  },
};
</script>
